'use client';

import {usePathname} from 'next/navigation';
import {useEffect} from 'react';

import {
  extractScrollTargetAttributes,
  findAllScrollTargets,
} from '@web-catalog/components/ScrollTarget/scrollUtils';

import {logTmrEvent} from '@web-catalog/utils/analytics';

const logScrollDepth = (logEvent: string, scrollDepth: string) => {
  logTmrEvent({logEvent, params: {scrollDepth}});
};

export const ScrollTargetObserver = () => {
  const pathname = usePathname();
  useEffect(() => {
    const scrollTargets = findAllScrollTargets();

    if (!scrollTargets.length) {
      return;
    }

    const targetsHit = new Set();

    const cleanupCallbacks = scrollTargets.map((target) => {
      const attributes = extractScrollTargetAttributes(target);

      if (!attributes) {
        return;
      }

      const {eventName, scrollDepth, threshold} = attributes;
      if (!window?.IntersectionObserver || !window?.MutationObserver) {
        return;
      }

      const intersectionObserver = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && !targetsHit.has(scrollDepth)) {
            targetsHit.add(scrollDepth);

            logScrollDepth(eventName, scrollDepth);
          }
        },
        {threshold},
      );

      const getIntersectionTarget = () => target.firstElementChild ?? target;
      let currentIntersectionTarget = getIntersectionTarget();
      intersectionObserver.observe(currentIntersectionTarget);

      /**
       * this is necessary to observe correctly in case scroll target has children that may render after initial render
       * (e.g. after omicron config fetched)
       */
      const mutationObserver = new MutationObserver(() => {
        const newIntersectionTarget = getIntersectionTarget();

        if (currentIntersectionTarget !== newIntersectionTarget) {
          intersectionObserver.unobserve(currentIntersectionTarget);
          intersectionObserver.observe(newIntersectionTarget);
          currentIntersectionTarget = newIntersectionTarget;
        }
      });

      mutationObserver.observe(target, {childList: true});

      return () => {
        intersectionObserver.disconnect();
        mutationObserver.disconnect();
      };
    });

    return () => {
      cleanupCallbacks.forEach((callback) => {
        if (callback) {
          callback();
        }
      });
    };
  }, [pathname]);

  return null;
};
